import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import PageHeader from '../components/PageHeader';
import Modal from '../components/Modal';
import SearchableDropdown from '../components/SearchableDropdown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare, faTrash, faBan } from '@fortawesome/free-solid-svg-icons';
import { fetchClientById, fetchClientOrders, fetchClientContracts, fetchClientInvoices, fetchClientNotes, fetchClientTasks, fetchClientInstallationActivity, createClientNote, updateClientNote, deleteClientNote, uploadClientNoteAttachment, clientNoteAttachmentDownloadUrl, voidClientNoteAttachment, unvoidClientNoteAttachment, updateClient, voidClient, convertClientToLead, fetchClientAttachments, uploadClientAttachment, voidClientAttachment, unvoidClientAttachment, clientAttachmentFileUrl, type ClientRow, type OrderRow, type ContractRow, type InvoiceRow, type ClientNoteRow, type ClientNoteAttachmentRow, type ClientTaskOption, type ClientInstallationActivity, type ClientAttachmentRow } from '../api/clients';
import AttachmentVoidModal from '../components/AttachmentVoidModal';
import { AttachmentVoidPill, AttachmentVoidReason } from '../components/AttachmentVoidNote';
import { useAttachmentVoid } from '../hooks/useAttachmentVoid';
import { isVoided } from '../api/attachmentVoid';
import { orderAssigneeNames } from '../api/orderApi';
import { formatDateTimePT, formatDatePT } from '../utils/timezone';
// Generic despite the name: Postgres DATE columns arrive as full ISO strings.
import { formatTaskDate, toDateInputValue } from '../utils/formatTaskDate';
import { fetchVerifiedUsers, type VerifiedUser } from '../api/users';
import { useAuth } from '../context/AuthContext';
import { INDUSTRIES } from '../constants/industries';
import { US_STATES, regionForState, stateName } from '../constants/usRegions';
import { SortableTh, StaticTh } from '../components/SortableTh';
import { useTableSortState } from '../hooks/useTableSort';
import { sortRows } from '../utils/tableSort';
import FileDropZone from '../components/FileDropZone';
import MarketingOptOutBadge from '../components/MarketingOptOutBadge';
import CreateOrderForm from './CreateOrder';
import './Page.css';
import './ClientDetail.css';
import './Orders.css';

/** What the browser can show inline; anything else is download-only. */
function attachmentPreviewKind(a: ClientAttachmentRow): 'image' | 'pdf' | 'none' {
  const mime = (a.mime_type || '').toLowerCase();
  const ext = a.name.split('.').pop()?.toLowerCase() ?? '';
  if (mime.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp'].includes(ext)) return 'image';
  if (mime === 'application/pdf' || ext === 'pdf') return 'pdf';
  return 'none';
}

function formatFileSize(bytes: number | null): string {
  if (bytes == null || bytes <= 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = bytes;
  let unit = 0;
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024;
    unit += 1;
  }
  return `${size < 10 && unit > 0 ? size.toFixed(1) : Math.round(size)} ${units[unit]}`;
}

/** API / drivers may return user_id as string; avoid hiding actions for the note author. */
function clientNoteAuthorIsCurrentUser(note: ClientNoteRow, current: { id: number } | null | undefined): boolean {
  if (!current || note.user_id == null) return false;
  const nid = typeof note.user_id === 'number' ? note.user_id : Number(note.user_id);
  return Number.isFinite(nid) && nid === current.id;
}

const EMPTY_CLIENT_ACTIVITY: ClientInstallationActivity = {
  inspections: [],
  installations: [],
  onsiteServices: [],
  signoffs: [],
  calls: [],
};

const ClientDetail: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { clientId } = useParams<{ clientId: string }>();
  const clientIdNum = clientId ? parseInt(clientId, 10) : null;

  const [apiClient, setApiClient] = useState<ClientRow | null>(null);
  const [clientOrders, setClientOrders] = useState<OrderRow[]>([]);
  const [clientContracts, setClientContracts] = useState<ContractRow[]>([]);
  const [clientInvoices, setClientInvoices] = useState<InvoiceRow[]>([]);
  const [clientNotes, setClientNotes] = useState<ClientNoteRow[]>([]);
  const [clientTasks, setClientTasks] = useState<ClientTaskOption[]>([]);
  const [installActivity, setInstallActivity] = useState<ClientInstallationActivity>(EMPTY_CLIENT_ACTIVITY);
  const [verifiedUsers, setVerifiedUsers] = useState<VerifiedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({
    products: false,
    orders: false,
    contracts: false,
    invoices: false,
    appointments: false,
    callLog: false,
    attachments: false,
    notes: false,
  });
  const toggleSection = (key: string) =>
    setCollapsedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  const [isAddNoteModalOpen, setIsAddNoteModalOpen] = useState(false);
  const [isEditNoteModalOpen, setIsEditNoteModalOpen] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [newNoteTaskId, setNewNoteTaskId] = useState<number | ''>('');
  const [noteSubmitting, setNoteSubmitting] = useState(false);
  const [noteError, setNoteError] = useState('');
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
  const [editNoteText, setEditNoteText] = useState('');
  const [editNoteTaskId, setEditNoteTaskId] = useState<number | ''>('');
  const [noteEditError, setNoteEditError] = useState('');
  const [noteUpdating, setNoteUpdating] = useState(false);
  const [isDeleteNoteModalOpen, setIsDeleteNoteModalOpen] = useState(false);
  const [deletingNoteId, setDeletingNoteId] = useState<number | null>(null);
  const [noteDeleting, setNoteDeleting] = useState(false);
  const [noteDeleteError, setNoteDeleteError] = useState('');
  const [newNoteFile, setNewNoteFile] = useState<File | null>(null);
  const [editNoteExtraFile, setEditNoteExtraFile] = useState<File | null>(null);
  const [clientAttachments, setClientAttachments] = useState<ClientAttachmentRow[]>([]);
  const [isUploadAttachmentModalOpen, setIsUploadAttachmentModalOpen] = useState(false);
  const [attachmentUploading, setAttachmentUploading] = useState(false);
  const [attachmentError, setAttachmentError] = useState('');
  const [previewAttachment, setPreviewAttachment] = useState<ClientAttachmentRow | null>(null);

  const clientData = apiClient;

  const [company, setCompany] = useState('');
  const [employee, setEmployee] = useState('');
  const [pointOfContact, setPointOfContact] = useState('');
  const [billingAddress, setBillingAddress] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [siteLocationAddress, setSiteLocationAddress] = useState('');
  const [notes, setNotes] = useState('');
  const [startDate, setStartDate] = useState('');
  const [firstContact, setFirstContact] = useState('');
  const [industry, setIndustry] = useState('');

  // Edit modal state (combined)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editCompany, setEditCompany] = useState('');
  const [editEmployee, setEditEmployee] = useState('');
  const [editSelectedUserId, setEditSelectedUserId] = useState<number | null>(null);
  const [editStartDate, setEditStartDate] = useState('');
  const [editFirstContact, setEditFirstContact] = useState('');
  const [editPointOfContact, setEditPointOfContact] = useState('');
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);
  const [clientMessage, setClientMessage] = useState('');
  const [editContactEmail, setEditContactEmail] = useState('');
  const [editContactPhone, setEditContactPhone] = useState('');
  const [editBillingAddress, setEditBillingAddress] = useState('');
  const [editSiteLocationAddress, setEditSiteLocationAddress] = useState('');
  const [editStateCode, setEditStateCode] = useState('');
  const [editIndustry, setEditIndustry] = useState('');
  const [voidModalOpen, setVoidModalOpen] = useState(false);
  const [voidReasonInput, setVoidReasonInput] = useState('');
  const [voidSubmitting, setVoidSubmitting] = useState(false);
  const [isCreateOrderModalOpen, setIsCreateOrderModalOpen] = useState(false);
  const [voidError, setVoidError] = useState('');
  const [convertModalOpen, setConvertModalOpen] = useState(false);
  const [convertSource, setConvertSource] = useState('');
  const [convertSubmitting, setConvertSubmitting] = useState(false);
  const [convertError, setConvertError] = useState('');

  const isVoid = apiClient?.client_status === 'Void';

  const openVoidModal = () => {
    setVoidReasonInput('');
    setVoidError('');
    setVoidModalOpen(true);
  };

  const closeVoidModal = () => {
    if (voidSubmitting) return;
    setVoidModalOpen(false);
    setVoidError('');
  };

  const handleVoidClient = async () => {
    if (!clientIdNum) return;
    const reason = voidReasonInput.trim();
    if (!reason) {
      setVoidError('Please enter a reason for voiding this client.');
      return;
    }
    setVoidSubmitting(true);
    setVoidError('');
    try {
      const updated = await voidClient(clientIdNum, reason);
      setApiClient(updated);
      setVoidModalOpen(false);
      setVoidReasonInput('');
    } catch (err) {
      setVoidError(err instanceof Error ? err.message : 'Failed to void client.');
    } finally {
      setVoidSubmitting(false);
    }
  };

  /** Merges the void columns back onto one of a note's files. */
  const applyNoteAttachmentUpdate = (attachment: ClientNoteAttachmentRow) => {
    setClientNotes((prev) =>
      prev.map((note) =>
        note.id !== attachment.client_note_id
          ? note
          : {
              ...note,
              attachments: (note.attachments ?? []).map((x) =>
                x.id === attachment.id ? { ...x, ...attachment } : x
              ),
            }
      )
    );
  };

  const noteAttachmentVoid = useAttachmentVoid<ClientNoteAttachmentRow>({
    noun: 'file',
    label: (a) => a.original_file_name,
    onVoid: async (a, reason) => {
      if (!clientIdNum || !user) return;
      applyNoteAttachmentUpdate(await voidClientNoteAttachment(clientIdNum, a.client_note_id, a.id, reason, user.id));
    },
    onUnvoid: async (a) => {
      if (!clientIdNum || !user) return;
      applyNoteAttachmentUpdate(await unvoidClientNoteAttachment(clientIdNum, a.client_note_id, a.id, user.id));
    },
    onError: (message) => alert(message),
  });

  const handleUploadClientAttachments = async (files: File[]) => {
    if (!clientIdNum || files.length === 0) return;
    setAttachmentUploading(true);
    setAttachmentError('');
    try {
      for (const file of files) {
        await uploadClientAttachment(clientIdNum, file, { userId: user?.id });
      }
      setClientAttachments(await fetchClientAttachments(clientIdNum));
      setIsUploadAttachmentModalOpen(false);
    } catch (err) {
      setAttachmentError(err instanceof Error ? err.message : 'Failed to upload file.');
    } finally {
      setAttachmentUploading(false);
    }
  };

  const folderAttachmentVoid = useAttachmentVoid<ClientAttachmentRow>({
    noun: 'file',
    label: (a) => a.name,
    onVoid: async (a, reason) => {
      if (!clientIdNum) return;
      const updated = await voidClientAttachment(clientIdNum, a.source_id, reason, user?.id);
      setClientAttachments((prev) => prev.map((row) => (row.key === a.key ? { ...row, ...updated } : row)));
    },
    onUnvoid: async (a) => {
      if (!clientIdNum) return;
      const updated = await unvoidClientAttachment(clientIdNum, a.source_id, user?.id);
      setClientAttachments((prev) => prev.map((row) => (row.key === a.key ? { ...row, ...updated } : row)));
    },
    onError: setAttachmentError,
  });

  // Voided files stay in the folder as history, below the ones still in play.
  const orderedClientAttachments = useMemo(
    () => [...clientAttachments].sort((a, b) => Number(isVoided(a)) - Number(isVoided(b))),
    [clientAttachments]
  );

  const ordersList = clientOrders;

  // Order line items (products) are not exposed by the API for client orders; show empty for now
  const clientProducts: Array<{ product: { id: number; productName: string; serialNumber?: string; status?: string }; orderNumber: string }> = useMemo(() => [], []);

  const clientProductsSort = useTableSortState();
  const sortedClientProducts = useMemo(
    () =>
      sortRows(clientProducts, clientProductsSort.sortKey, clientProductsSort.sortDir, (row, key) => {
        switch (key) {
          case 'order':
            return row.orderNumber;
          case 'product':
            return row.product.productName;
          case 'serial':
            return row.product.serialNumber ?? '';
          case 'status':
            return row.product.status ?? '';
          default:
            return '';
        }
      }),
    [clientProducts, clientProductsSort.sortKey, clientProductsSort.sortDir]
  );

  const clientOrdersSort = useTableSortState();
  const sortedClientOrders = useMemo(
    () =>
      sortRows(ordersList, clientOrdersSort.sortKey, clientOrdersSort.sortDir, (order, key) => {
        const orderNumber = 'order_number' in order ? order.order_number : (order as { orderNumber: string }).orderNumber;
        const category = 'category' in order ? order.category : (order as { category: string }).category;
        const status = 'status' in order ? order.status : (order as { status: string }).status;
        const emp = 'employee_name' in order ? orderAssigneeNames(order as OrderRow).join(', ') : (order as { employee?: string }).employee;
        switch (key) {
          case 'order_number':
            return orderNumber;
          case 'stage':
            return category;
          case 'status':
            return status;
          case 'employee':
            return emp ?? '';
          default:
            return '';
        }
      }),
    [ordersList, clientOrdersSort.sortKey, clientOrdersSort.sortDir]
  );

  const clientContractsSort = useTableSortState();
  const sortedClientContracts = useMemo(
    () =>
      sortRows(clientContracts, clientContractsSort.sortKey, clientContractsSort.sortDir, (c, key) => {
        switch (key) {
          case 'order_number':
            return c.order_number;
          case 'type':
            return c.contract_type ?? '';
          case 'status':
            return c.status;
          case 'generated':
            return c.generated_at ? new Date(c.generated_at).getTime() : 0;
          case 'signed':
            return c.signed_at ? new Date(c.signed_at).getTime() : 0;
          default:
            return '';
        }
      }),
    [clientContracts, clientContractsSort.sortKey, clientContractsSort.sortDir]
  );

  const clientInvoicesSort = useTableSortState();
  const sortedClientInvoices = useMemo(
    () =>
      sortRows(clientInvoices, clientInvoicesSort.sortKey, clientInvoicesSort.sortDir, (inv, key) => {
        switch (key) {
          case 'order_number':
            return inv.order_number;
          case 'invoice_number':
            return inv.invoice_number ?? '';
          case 'status':
            return inv.status;
          case 'date':
            return inv.created_at ? new Date(inv.created_at).getTime() : 0;
          default:
            return '';
        }
      }),
    [clientInvoices, clientInvoicesSort.sortKey, clientInvoicesSort.sortDir]
  );

  const clientNotesSort = useTableSortState();
  const sortedClientNotes = useMemo(
    () =>
      sortRows(clientNotes, clientNotesSort.sortKey, clientNotesSort.sortDir, (n, key) => {
        switch (key) {
          case 'user':
            return n.submitted_by ?? '';
          case 'created_at':
            return new Date(n.created_at).getTime();
          case 'task':
            return n.task_name ?? '';
          case 'note':
            return n.note;
          default:
            return '';
        }
      }),
    [clientNotes, clientNotesSort.sortKey, clientNotesSort.sortDir]
  );

  // Calculate relationship duration
  const calculateRelationshipDuration = (startDateStr: string): string => {
    if (!startDateStr) return 'Not set';
    
    const start = new Date(startDateStr);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - start.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 30) {
      return `${diffDays} day${diffDays !== 1 ? 's' : ''}`;
    } else if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      return `${months} month${months !== 1 ? 's' : ''}`;
    } else {
      const years = Math.floor(diffDays / 365);
      const remainingMonths = Math.floor((diffDays % 365) / 30);
      if (remainingMonths === 0) {
        return `${years} year${years !== 1 ? 's' : ''}`;
      } else {
        return `${years} year${years !== 1 ? 's' : ''}, ${remainingMonths} month${remainingMonths !== 1 ? 's' : ''}`;
      }
    }
  };

  useEffect(() => {
    if (!clientIdNum) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const c = await fetchClientById(clientIdNum);
        if (cancelled) return;
        if (!c) {
          setNotFound(true);
          setApiClient(null);
          setLoading(false);
          return;
        }
        if (c.type !== 'client') {
          setNotFound(true);
          setApiClient(null);
          setLoading(false);
          return;
        }
        setApiClient(c);
        const settled = await Promise.allSettled([
          fetchClientOrders(clientIdNum),
          fetchClientContracts(clientIdNum),
          fetchClientInvoices(clientIdNum),
          fetchClientNotes(clientIdNum),
          fetchClientTasks(clientIdNum),
          fetchClientInstallationActivity(clientIdNum),
          fetchClientAttachments(clientIdNum),
        ]);
        if (!cancelled) {
          setClientOrders(settled[0].status === 'fulfilled' ? settled[0].value : []);
          setClientContracts(settled[1].status === 'fulfilled' ? settled[1].value : []);
          setClientInvoices(settled[2].status === 'fulfilled' ? settled[2].value : []);
          setClientNotes(settled[3].status === 'fulfilled' ? settled[3].value : []);
          setClientTasks(settled[4].status === 'fulfilled' ? settled[4].value : []);
          setInstallActivity(settled[5].status === 'fulfilled' ? settled[5].value : EMPTY_CLIENT_ACTIVITY);
          setClientAttachments(settled[6].status === 'fulfilled' ? settled[6].value : []);
        }
      } catch {
        if (!cancelled) {
          setNotFound(true);
          setApiClient(null);
          setClientOrders([]);
          setClientContracts([]);
          setClientInvoices([]);
          setClientNotes([]);
          setClientTasks([]);
          setInstallActivity(EMPTY_CLIENT_ACTIVITY);
          setClientAttachments([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [clientIdNum]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const users = await fetchVerifiedUsers();
        if (!cancelled) setVerifiedUsers(users);
      } catch {
        if (!cancelled) setVerifiedUsers([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (clientData) {
      const companyVal = 'company' in clientData ? clientData.company : (clientData as { company: string }).company;
      const employeeVal = 'employee_name' in clientData ? (clientData as ClientRow).employee_name : (clientData as { employee?: string }).employee;
      const pocVal = 'point_of_contact' in clientData ? (clientData as ClientRow).point_of_contact : (clientData as { pointOfContact: string }).pointOfContact;
      const emailVal = 'contact_email' in clientData ? (clientData as ClientRow).contact_email : (clientData as { contactEmail?: string }).contactEmail;
      const phoneVal = 'contact_phone' in clientData ? (clientData as ClientRow).contact_phone : (clientData as { contactPhone?: string }).contactPhone;
      const notesVal = 'notes' in clientData ? clientData.notes ?? '' : (clientData as { notes?: string }).notes ?? '';
      const startVal = 'start_date' in clientData ? (clientData as ClientRow).start_date : (clientData as { startDate?: string }).startDate;
      // Postgres DATE arrives as a full ISO string, which a date input rejects.
      const firstContactVal = toDateInputValue((clientData as ClientRow).first_contact);
      const billingVal = 'billing_address' in clientData ? (clientData as ClientRow).billing_address ?? '' : '';
      const siteVal = 'site_location' in clientData ? (clientData as ClientRow).site_location ?? '' : '';
      const industryVal = 'industry' in clientData ? (clientData as ClientRow).industry ?? '' : '';

      setCompany(companyVal);
      setEmployee(employeeVal ?? '');
      setPointOfContact(pocVal);
      setContactEmail(emailVal ?? '');
      setContactPhone(phoneVal ?? '');
      setNotes(notesVal);
      setStartDate(startVal ?? '');
      setFirstContact(firstContactVal);
      setBillingAddress(billingVal);
      setSiteLocationAddress(siteVal);
      setIndustry(industryVal);

      setEditEmployee(employeeVal ?? '');
      setEditStartDate(startVal ?? '');
      setEditFirstContact(firstContactVal);
      setEditIndustry(industryVal);
      setEditPointOfContact(pocVal);
      setEditContactEmail(emailVal ?? '');
      setEditContactPhone(phoneVal ?? '');
      setEditBillingAddress(billingVal);
      setEditSiteLocationAddress(siteVal);
      setEditStateCode('state_code' in clientData ? (clientData as ClientRow).state_code ?? '' : '');
    }
  }, [clientData]);

  useEffect(() => {
    if (!clientData || verifiedUsers.length === 0) return;
    const employeeName = 'employee_name' in clientData ? (clientData as ClientRow).employee_name : (clientData as { employee?: string }).employee;
    const name = employeeName ?? '';
    const u = verifiedUsers.find((x) => x.username === name);
    setEditSelectedUserId(u ? u.id : null);
  }, [clientData, verifiedUsers]);

  const [editNotes, setEditNotes] = useState('');

  const clientRegion = useMemo(() => regionForState(apiClient?.state_code), [apiClient?.state_code]);
  const editRegion = useMemo(() => regionForState(editStateCode), [editStateCode]);

  const handleEditClick = () => {
    setSaveError('');
    setEditCompany(company);
    setEditIndustry(industry);
    setEditNotes(notes);
    setIsEditModalOpen(true);
  };

  const handleEditSave = async () => {
    setSaveError('');
    if (!clientIdNum) {
      setEmployee(editEmployee);
      setStartDate(editStartDate);
      setPointOfContact(editPointOfContact);
      setContactEmail(editContactEmail);
      setContactPhone(editContactPhone);
      setBillingAddress(editBillingAddress);
      setSiteLocationAddress(editSiteLocationAddress);
      setIsEditModalOpen(false);
      return;
    }
    if (!editCompany.trim()) {
      setSaveError('Company name cannot be empty.');
      return;
    }
    setSaving(true);
    try {
      const payload: Parameters<typeof updateClient>[1] = {
        company: editCompany.trim(),
        start_date: editStartDate.trim() || null,
        first_contact: editFirstContact.trim() || null,
        industry: editIndustry.trim() || null,
        point_of_contact: editPointOfContact.trim(),
        contact_email: editContactEmail.trim() || null,
        contact_phone: editContactPhone.trim() || null,
        billing_address: editBillingAddress.trim() || null,
        site_location: editSiteLocationAddress.trim() || null,
        state_code: editStateCode || null,
        notes: editNotes.trim() || null,
      };
      if (editSelectedUserId != null) payload.user_id = editSelectedUserId;
      else if (editEmployee.trim()) payload.employee_name = editEmployee.trim();
      else payload.employee_name = null;
      const updated = await updateClient(clientIdNum, payload);
      setApiClient(updated);
      setCompany(updated.company);
      setEditCompany(updated.company);
      setEmployee(updated.employee_name ?? '');
      setStartDate(updated.start_date ?? '');
      setFirstContact(toDateInputValue(updated.first_contact));
      setIndustry(updated.industry ?? '');
      setPointOfContact(updated.point_of_contact);
      setContactEmail(updated.contact_email ?? '');
      setContactPhone(updated.contact_phone ?? '');
      setBillingAddress(updated.billing_address ?? '');
      setSiteLocationAddress(updated.site_location ?? '');
      setEditEmployee(updated.employee_name ?? '');
      setEditStartDate(updated.start_date ?? '');
      setEditFirstContact(toDateInputValue(updated.first_contact));
      setEditIndustry(updated.industry ?? '');
      setEditPointOfContact(updated.point_of_contact);
      setEditContactEmail(updated.contact_email ?? '');
      setEditContactPhone(updated.contact_phone ?? '');
      setEditBillingAddress(updated.billing_address ?? '');
      setEditSiteLocationAddress(updated.site_location ?? '');
      setNotes(updated.notes ?? '');
      setEditNotes(updated.notes ?? '');
      setIsEditModalOpen(false);
      setClientMessage('Client updated successfully.');
      setTimeout(() => setClientMessage(''), 4000);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to update client.');
    } finally {
      setSaving(false);
    }
  };

  /**
   * What is already hanging off this client, so demoting it to a lead is not a
   * blind move. Read from data the page has already loaded rather than asking
   * the server again.
   */
  const attachedWorkSummary = useMemo(() => {
    const appointments =
      installActivity.inspections.length +
      installActivity.installations.length +
      installActivity.onsiteServices.length;
    return [
      { noun: 'order', count: clientOrders.length },
      { noun: 'contract', count: clientContracts.length },
      { noun: 'invoice', count: clientInvoices.length },
      { noun: 'appointment', count: appointments },
    ]
      .filter((entry) => entry.count > 0)
      .map(({ noun, count }) => `${count} ${noun}${count === 1 ? '' : 's'}`)
      .join(', ');
  }, [clientOrders, clientContracts, clientInvoices, installActivity]);

  /** A lead with no source is not workable, so one is required when none exists. */
  const convertNeedsSource = !(apiClient?.source ?? '').trim();

  const openConvertModal = () => {
    setConvertSource(apiClient?.source ?? '');
    setConvertError('');
    setConvertModalOpen(true);
  };

  const closeConvertModal = () => {
    if (convertSubmitting) return;
    setConvertModalOpen(false);
    setConvertError('');
  };

  const handleConvertToLead = async () => {
    if (!clientIdNum) return;
    setConvertSubmitting(true);
    setConvertError('');
    try {
      await convertClientToLead(clientIdNum, convertSource);
      // Same row, so the id carries over; this page only renders clients.
      navigate(`/lead/${clientIdNum}`);
    } catch (err) {
      setConvertError(err instanceof Error ? err.message : 'Failed to convert client to lead.');
    } finally {
      setConvertSubmitting(false);
    }
  };

  const handleEditCancel = () => {
    setEditCompany(company);
    setEditEmployee(employee);
    setEditStartDate(startDate);
    setEditFirstContact(firstContact);
    setEditIndustry(industry);
    setEditPointOfContact(pointOfContact);
    setEditContactEmail(contactEmail);
    setEditContactPhone(contactPhone);
    setEditBillingAddress(billingAddress);
    setEditSiteLocationAddress(siteLocationAddress);
    setEditNotes(notes);
    setSaveError('');
    setIsEditModalOpen(false);
  };

  if (loading && !clientData) {
    return (
      <div className="page-container">
        <div className="page-layout">
          <Sidebar />
          <main className="page-main">
            <div className="page-content">
              <h2 className="page-title">Client Details</h2>
              <p className="page-subtitle">Loading...</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (notFound || !clientData) {
    return (
      <div className="page-container">
        <div className="page-layout">
          <Sidebar />
          <main className="page-main">
            <div className="page-content">
              <h2 className="page-title">Client Not Found</h2>
              <button onClick={() => navigate('/client')}>Back to Clients</button>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-layout">
        <Sidebar />
        <main className="page-main">
          <PageHeader
            title={company || 'Client Details'}
            subtitle="View and update client information"
            onBack={() => navigate('/client')}
            backLabel="Back"
          />
          <div className="page-content">
            {/* Combined Client Information Card */}
            <div className="client-info-card">
              <div className="client-info-header">
                <h3 className="section-title">Client Information</h3>
                <div className="client-action-buttons">
                  <button
                    type="button"
                    className="client-action-btn client-action-btn--edit"
                    data-tooltip="Edit client"
                    onClick={handleEditClick}
                    aria-label="Edit client"
                  >
                    <FontAwesomeIcon icon={faPenToSquare} />
                  </button>
                  {!isVoid ? (
                    <button
                      type="button"
                      className="client-action-btn client-action-btn--void"
                      data-tooltip="Void client"
                      onClick={openVoidModal}
                      aria-label="Void client"
                    >
                      <FontAwesomeIcon icon={faBan} />
                    </button>
                  ) : null}
                </div>
              </div>

              {isVoid && apiClient?.void_reason ? (
                <div className="client-info-void-banner" role="status">
                  <strong>Client voided.</strong>{' '}
                  <span className="client-info-void-reason-label">Reason:</span>{' '}
                  <span className="client-info-void-reason">{apiClient.void_reason}</span>
                </div>
              ) : null}

              <div className="client-info-grid">
                <div className="client-info-item">
                  <label className="client-info-label">Company</label>
                  <div className="client-info-value">{company}</div>
                </div>
                <div className="client-info-item">
                  <label className="client-info-label">Employee</label>
                  <div className="client-info-value">{employee || 'Unassigned'}</div>
                </div>
                <div className="client-info-item">
                  <label className="client-info-label">Relationship Duration</label>
                  <div className="client-info-value">{calculateRelationshipDuration(startDate)}</div>
                </div>
                <div className="client-info-item">
                  <label className="client-info-label">First Contact</label>
                  <div className="client-info-value">{formatTaskDate(firstContact)}</div>
                </div>
                <div className="client-info-item">
                  <label className="client-info-label">Industry</label>
                  <div className="client-info-value">{industry || '—'}</div>
                </div>
                <div className="client-info-item">
                  <label className="client-info-label">Job Code / Deployment ID</label>
                  <div className="client-info-value">
                    {apiClient?.job_code ? (
                      <span className="job-code">{apiClient.job_code}</span>
                    ) : (
                      <span className="client-info-value-muted">
                        {apiClient?.customer_number == null
                          ? 'Not a client yet'
                          : 'Add a state to generate a code'}
                      </span>
                    )}
                  </div>
                </div>
                <div className="client-info-item">
                  <label className="client-info-label">State / Region</label>
                  <div className="client-info-value">
                    {clientRegion
                      ? `${stateName(apiClient?.state_code) ?? apiClient?.state_code} — ${clientRegion.label}`
                      : 'Not set'}
                  </div>
                </div>
              </div>

              <hr className="client-info-divider" />

              <h4 className="client-info-subsection-title">Point of Contact</h4>
              <div className="client-info-grid">
                <div className="client-info-item">
                  <label className="client-info-label">Name</label>
                  <div className="client-info-value">{pointOfContact}</div>
                </div>
                <div className="client-info-item">
                  <label className="client-info-label">Email</label>
                  <div className="client-info-value">
                    {contactEmail || 'Not set'}
                    <MarketingOptOutBadge client={apiClient} />
                  </div>
                </div>
                <div className="client-info-item">
                  <label className="client-info-label">Phone</label>
                  <div className="client-info-value">{contactPhone || 'Not set'}</div>
                </div>
                <div className="client-info-item">
                  <label className="client-info-label">Billing Address</label>
                  <div className="client-info-value">{billingAddress || 'Not set'}</div>
                </div>
                <div className="client-info-item">
                  <label className="client-info-label">Site Location Address</label>
                  <div className="client-info-value">{siteLocationAddress || 'Not set'}</div>
                </div>
              </div>

              {notes && (
                <>
                  <hr className="client-info-divider" />
                  <h4 className="client-info-subsection-title">Notes</h4>
                  <p className="client-info-value" style={{ whiteSpace: 'pre-wrap' }}>{notes}</p>
                </>
              )}
            </div>

            {clientMessage && (
              <p
                className="update-message-banner settings-success"
                role="alert"
                style={{ marginTop: '0.5rem', marginBottom: '0.5rem' }}
              >
                {clientMessage}
              </p>
            )}

            {/* Products Section */}
            {clientProducts.length > 0 && (
              <div className="order-detail-table-section">
                <div className="collapsible-header" onClick={() => toggleSection('products')}>
                  <h3 className="section-title">Products</h3>
                  <span className={`collapse-arrow${!collapsedSections.products ? ' collapse-arrow--open' : ''}`}>▶</span>
                </div>
                {!collapsedSections.products && <div className="orders-table-wrapper">
                  <table className="orders-table">
                    <thead>
                      <tr>
                        <SortableTh label="Order Number" columnKey="order" sortKey={clientProductsSort.sortKey} sortDir={clientProductsSort.sortDir} onSort={clientProductsSort.toggleSort} />
                        <SortableTh label="Product Name" columnKey="product" sortKey={clientProductsSort.sortKey} sortDir={clientProductsSort.sortDir} onSort={clientProductsSort.toggleSort} />
                        <SortableTh label="Serial Number" columnKey="serial" sortKey={clientProductsSort.sortKey} sortDir={clientProductsSort.sortDir} onSort={clientProductsSort.toggleSort} />
                        <SortableTh label="Status" columnKey="status" sortKey={clientProductsSort.sortKey} sortDir={clientProductsSort.sortDir} onSort={clientProductsSort.toggleSort} />
                      </tr>
                    </thead>
                    <tbody>
                      {sortedClientProducts.map(({ product, orderNumber }, index) => (
                        <tr key={`${product.id}-${index}`}>
                          <td data-label="Order Number">
                            <Link to={`/orders/${orderNumber}`} className="client-order-link">{orderNumber}</Link>
                          </td>
                          <td data-label="Product">{product.productName}</td>
                          <td data-label="Serial Number">{product.serialNumber || 'N/A'}</td>
                          <td data-label="Status">
                            <span className={`status-badge status-${(product.status || 'pending').toLowerCase().replace(' ', '-')}`}>
                              {product.status || 'Pending'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>}
              </div>
            )}

            {/* Orders Section */}
            <div className="order-detail-table-section">
                <div className="collapsible-header" onClick={() => toggleSection('orders')}>
                  <h3 className="section-title">Orders</h3>
                  <div className="client-orders-header-actions">
                    {!isVoid && (
                      <button
                        type="button"
                        className="update-button client-create-order-btn"
                        onClick={(e) => { e.stopPropagation(); setIsCreateOrderModalOpen(true); }}
                      >
                        + Create Order
                      </button>
                    )}
                    <span className={`collapse-arrow${!collapsedSections.orders ? ' collapse-arrow--open' : ''}`}>▶</span>
                  </div>
                </div>
                {!collapsedSections.orders && (
                  ordersList.length > 0 ? (
                <div className="orders-table-wrapper">
                  <table className="orders-table">
                    <thead>
                      <tr>
                        <SortableTh label="Order Number" columnKey="order_number" sortKey={clientOrdersSort.sortKey} sortDir={clientOrdersSort.sortDir} onSort={clientOrdersSort.toggleSort} />
                        <SortableTh label="Stage" columnKey="stage" sortKey={clientOrdersSort.sortKey} sortDir={clientOrdersSort.sortDir} onSort={clientOrdersSort.toggleSort} />
                        <SortableTh label="Status" columnKey="status" sortKey={clientOrdersSort.sortKey} sortDir={clientOrdersSort.sortDir} onSort={clientOrdersSort.toggleSort} />
                        <SortableTh label="Employee" columnKey="employee" sortKey={clientOrdersSort.sortKey} sortDir={clientOrdersSort.sortDir} onSort={clientOrdersSort.toggleSort} />
                      </tr>
                    </thead>
                    <tbody>
                      {sortedClientOrders.map((order) => {
                        const orderNumber = 'order_number' in order ? order.order_number : (order as { orderNumber: string }).orderNumber;
                        const category = 'category' in order ? order.category : (order as { category: string }).category;
                        const status = 'status' in order ? order.status : (order as { status: string }).status;
                        const emp = 'employee_name' in order ? orderAssigneeNames(order as OrderRow).join(', ') : (order as { employee?: string }).employee;
                        return (
                          <tr key={order.id}>
                            <td data-label="Order Number">
                              <Link to={`/orders/${orderNumber}`} className="client-order-link">{orderNumber}</Link>
                            </td>
                            <td data-label="Stage">{category}</td>
                            <td data-label="Status">
                              <span className={`status-badge status-${String(status).toLowerCase().replace(/\s+/g, '-')}`}>
                                {status}
                              </span>
                            </td>
                            <td data-label="Employee">{emp || 'unassigned'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                  ) : (
                    <p className="page-subtitle client-orders-empty">No orders yet. Create one to get started.</p>
                  )
                )}
              </div>

            {/* Contracts Section */}
            {clientContracts.length > 0 && (
              <div className="order-detail-table-section">
                <div className="collapsible-header" onClick={() => toggleSection('contracts')}>
                  <h3 className="section-title">Contracts</h3>
                  <span className={`collapse-arrow${!collapsedSections.contracts ? ' collapse-arrow--open' : ''}`}>▶</span>
                </div>
                {!collapsedSections.contracts && (
                <div className="orders-table-wrapper">
                  <table className="orders-table">
                    <thead>
                      <tr>
                        <SortableTh label="Order Number" columnKey="order_number" sortKey={clientContractsSort.sortKey} sortDir={clientContractsSort.sortDir} onSort={clientContractsSort.toggleSort} />
                        <SortableTh label="Type" columnKey="type" sortKey={clientContractsSort.sortKey} sortDir={clientContractsSort.sortDir} onSort={clientContractsSort.toggleSort} />
                        <SortableTh label="Status" columnKey="status" sortKey={clientContractsSort.sortKey} sortDir={clientContractsSort.sortDir} onSort={clientContractsSort.toggleSort} />
                        <SortableTh label="Generated" columnKey="generated" sortKey={clientContractsSort.sortKey} sortDir={clientContractsSort.sortDir} onSort={clientContractsSort.toggleSort} />
                        <SortableTh label="Signed" columnKey="signed" sortKey={clientContractsSort.sortKey} sortDir={clientContractsSort.sortDir} onSort={clientContractsSort.toggleSort} />
                      </tr>
                    </thead>
                    <tbody>
                      {sortedClientContracts.map((c) => (
                        <tr key={c.id}>
                          <td data-label="Order Number">
                            <Link to={`/orders/${c.order_number}`} className="client-order-link">{c.order_number}</Link>
                          </td>
                          <td data-label="Type">{c.contract_type ?? '—'}</td>
                          <td data-label="Status">
                            <span className={`status-badge status-${String(c.status).toLowerCase().replace(/\s+/g, '-')}`}>
                              {c.status}
                            </span>
                          </td>
                          <td data-label="Generated">{formatDatePT(c.generated_at)}</td>
                          <td data-label="Signed">{formatDatePT(c.signed_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                )}
              </div>
            )}

            {/* Invoices Section */}
            {clientInvoices.length > 0 && (
              <div className="order-detail-table-section">
                <div className="collapsible-header" onClick={() => toggleSection('invoices')}>
                  <h3 className="section-title">Invoices</h3>
                  <span className={`collapse-arrow${!collapsedSections.invoices ? ' collapse-arrow--open' : ''}`}>▶</span>
                </div>
                {!collapsedSections.invoices && (
                <div className="orders-table-wrapper">
                  <table className="orders-table">
                    <thead>
                      <tr>
                        <SortableTh label="Order Number" columnKey="order_number" sortKey={clientInvoicesSort.sortKey} sortDir={clientInvoicesSort.sortDir} onSort={clientInvoicesSort.toggleSort} />
                        <SortableTh label="Invoice Number" columnKey="invoice_number" sortKey={clientInvoicesSort.sortKey} sortDir={clientInvoicesSort.sortDir} onSort={clientInvoicesSort.toggleSort} />
                        <SortableTh label="Status" columnKey="status" sortKey={clientInvoicesSort.sortKey} sortDir={clientInvoicesSort.sortDir} onSort={clientInvoicesSort.toggleSort} />
                        <SortableTh label="Date" columnKey="date" sortKey={clientInvoicesSort.sortKey} sortDir={clientInvoicesSort.sortDir} onSort={clientInvoicesSort.toggleSort} />
                      </tr>
                    </thead>
                    <tbody>
                      {sortedClientInvoices.map((inv) => (
                        <tr key={inv.id}>
                          <td data-label="Order Number">
                            <Link to={`/orders/${inv.order_number}`} className="client-order-link">{inv.order_number}</Link>
                          </td>
                          <td data-label="Invoice Number">{inv.invoice_number ?? '—'}</td>
                          <td data-label="Status">
                            <span className={`status-badge status-${String(inv.status).toLowerCase().replace(/\s+/g, '-')}`}>
                              {inv.status}
                            </span>
                          </td>
                          <td data-label="Date">{formatDatePT(inv.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                )}
              </div>
            )}

            {/* Appointments (site inspections, installations, onsite service, sign-offs) */}
            {(installActivity.inspections.length > 0 ||
              installActivity.installations.length > 0 ||
              installActivity.onsiteServices.length > 0 ||
              installActivity.signoffs.length > 0) && (
              <div className="order-detail-table-section">
                <div className="collapsible-header" onClick={() => toggleSection('appointments')}>
                  <h3 className="section-title">Appointments</h3>
                  <span className={`collapse-arrow${!collapsedSections.appointments ? ' collapse-arrow--open' : ''}`}>▶</span>
                </div>
                {!collapsedSections.appointments && (
                  <div className="orders-table-wrapper">
                    <table className="orders-table">
                      <thead>
                        <tr>
                          <StaticTh>Type</StaticTh>
                          <StaticTh>Business</StaticTh>
                          <StaticTh>Date</StaticTh>
                          <StaticTh>Status</StaticTh>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          ...installActivity.inspections.map((r) => ({ ...r, kind: 'Site Inspection', to: `/installation/site-inspection/${r.id}` })),
                          ...installActivity.installations.map((r) => ({ ...r, kind: 'Installation', to: `/installation/${r.id}` })),
                          ...installActivity.onsiteServices.map((r) => ({ ...r, kind: 'Onsite Service', to: `/installation/onsite-service/${r.id}` })),
                          ...installActivity.signoffs.map((r) => ({ ...r, kind: 'Customer Sign-Off', to: `/installation/sign-off/${r.id}` })),
                        ].map((r) => (
                          <tr
                            key={`${r.kind}-${r.id}`}
                            className="events-table-row-clickable"
                            onClick={() => navigate(r.to)}
                            style={{ cursor: 'pointer' }}
                          >
                            <td data-label="Type">{r.kind}</td>
                            <td data-label="Business">{r.business_name}</td>
                            <td data-label="Date">{r.when ? formatDatePT(r.when) : '—'}</td>
                            <td data-label="Status">
                              <span className={`status-badge status-${String(r.status).toLowerCase().replace(/\s+/g, '-')}`}>
                                {r.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Call Log: scheduled and logged calls for this client */}
            {installActivity.calls.length > 0 && (
              <div className="order-detail-table-section">
                <div className="collapsible-header" onClick={() => toggleSection('callLog')}>
                  <h3 className="section-title">Call Log</h3>
                  <span className={`collapse-arrow${!collapsedSections.callLog ? ' collapse-arrow--open' : ''}`}>▶</span>
                </div>
                {!collapsedSections.callLog && (
                  <div className="orders-table-wrapper">
                    <table className="orders-table">
                      <thead>
                        <tr>
                          <StaticTh>Employee</StaticTh>
                          <StaticTh>Contact</StaticTh>
                          <StaticTh>When</StaticTh>
                          <StaticTh>Reason</StaticTh>
                          <StaticTh>Status</StaticTh>
                        </tr>
                      </thead>
                      <tbody>
                        {installActivity.calls.map((c) => (
                          <tr key={c.id}>
                            <td data-label="Employee">{c.employee_name || '—'}</td>
                            {/* A scheduled call has no one spoken to yet, so fall
                                back to who the employee is meant to reach. */}
                            <td data-label="Contact">{c.spoke_with || c.point_of_contact || '—'}</td>
                            <td data-label="When">{c.when ? formatDateTimePT(c.when) : '—'}</td>
                            <td data-label="Reason">{c.reason || '—'}</td>
                            <td data-label="Status">
                              <span className={`status-badge status-${c.status.toLowerCase()}`}>{c.status}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Attachments: every file tied to this client, wherever it was created */}
            <div className="order-detail-table-section">
              <div className="collapsible-header">
                <div className="collapsible-header-left" onClick={() => toggleSection('attachments')}>
                  <h3 className="section-title">Attachments</h3>
                  <span className={`collapse-arrow${!collapsedSections.attachments ? ' collapse-arrow--open' : ''}`}>▶</span>
                </div>
                <button
                  type="button"
                  className="section-header-btn"
                  onClick={() => {
                    setAttachmentError('');
                    setIsUploadAttachmentModalOpen(true);
                  }}
                >
                  + Upload File
                </button>
              </div>
              {!collapsedSections.attachments && (
                <div className="orders-table-wrapper">
                  <table className="orders-table client-attachments-table">
                    <thead>
                      <tr>
                        <StaticTh>File</StaticTh>
                        <StaticTh>Type</StaticTh>
                        <StaticTh>Order</StaticTh>
                        <StaticTh>Size</StaticTh>
                        <StaticTh>Added</StaticTh>
                        <StaticTh>Actions</StaticTh>
                      </tr>
                    </thead>
                    <tbody>
                      {clientAttachments.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="client-notes-empty">No files yet.</td>
                        </tr>
                      ) : (
                        orderedClientAttachments.map((a) => (
                          <tr key={a.key} className={isVoided(a) ? 'attachment-voided' : undefined}>
                            <td data-label="File" className="client-attachment-name">
                              <span className="attachment-void-name">{a.name}</span>{' '}
                              {isVoided(a) ? <AttachmentVoidPill /> : null}
                              <AttachmentVoidReason attachment={a} />
                            </td>
                            <td data-label="Type">{a.category}</td>
                            <td data-label="Order">
                              {a.order_number ? <Link to={`/orders/${a.order_number}`}>{a.order_number}</Link> : '—'}
                            </td>
                            <td data-label="Size">{formatFileSize(a.byte_size)}</td>
                            <td data-label="Added">
                              {a.created_at ? formatDateTimePT(a.created_at) : '—'}
                              {a.uploaded_by ? ` · ${a.uploaded_by}` : ''}
                            </td>
                            <td data-label="Actions" className="client-notes-actions-cell">
                              <div className="client-notes-actions-inner">
                                {attachmentPreviewKind(a) !== 'none' ? (
                                  <button
                                    type="button"
                                    className="client-attachment-link"
                                    onClick={() => setPreviewAttachment(a)}
                                  >
                                    View
                                  </button>
                                ) : null}
                                <a
                                  className="client-attachment-link"
                                  href={clientIdNum != null ? clientAttachmentFileUrl(clientIdNum, a) : '#'}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  New Tab
                                </a>
                                <a
                                  className="client-attachment-link"
                                  href={clientIdNum != null ? clientAttachmentFileUrl(clientIdNum, a, { download: true }) : '#'}
                                >
                                  Download
                                </a>
                                {a.can_void ? (
                                  <button
                                    type="button"
                                    className={isVoided(a) ? 'attachment-unvoid-btn' : 'attachment-void-btn'}
                                    disabled={folderAttachmentVoid.busy}
                                    onClick={() =>
                                      isVoided(a)
                                        ? folderAttachmentVoid.unvoid(a)
                                        : folderAttachmentVoid.requestVoid(a)
                                    }
                                  >
                                    {isVoided(a) ? 'Unvoid' : 'Void'}
                                  </button>
                                ) : null}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Notes Section */}
            <div className="order-detail-table-section">
              <div className="collapsible-header">
                <div className="collapsible-header-left" onClick={() => toggleSection('notes')}>
                  <h3 className="section-title">Notes</h3>
                  <span className={`collapse-arrow${!collapsedSections.notes ? ' collapse-arrow--open' : ''}`}>▶</span>
                </div>
                <button
                  type="button"
                  className="section-header-btn"
                  onClick={() => setIsAddNoteModalOpen(true)}
                >
                  + Add Note
                </button>
              </div>
              {!collapsedSections.notes && (
                <div className="orders-table-wrapper">
                  <table className="orders-table client-notes-table">
                    <thead>
                      <tr>
                        <SortableTh label="User" columnKey="user" sortKey={clientNotesSort.sortKey} sortDir={clientNotesSort.sortDir} onSort={clientNotesSort.toggleSort} />
                        <SortableTh label="Date & Time" columnKey="created_at" sortKey={clientNotesSort.sortKey} sortDir={clientNotesSort.sortDir} onSort={clientNotesSort.toggleSort} />
                        <SortableTh label="Task" columnKey="task" sortKey={clientNotesSort.sortKey} sortDir={clientNotesSort.sortDir} onSort={clientNotesSort.toggleSort} />
                        <SortableTh label="Note" columnKey="note" sortKey={clientNotesSort.sortKey} sortDir={clientNotesSort.sortDir} onSort={clientNotesSort.toggleSort} />
                        <StaticTh>Files</StaticTh>
                        <StaticTh>Actions</StaticTh>
                      </tr>
                    </thead>
                    <tbody>
                      {clientNotes.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="client-notes-empty">No notes yet.</td>
                        </tr>
                      ) : (
                        sortedClientNotes.map((n) => (
                          <tr key={n.id}>
                            <td>{n.submitted_by ?? '—'}</td>
                            <td>{formatDateTimePT(n.created_at)}</td>
                            <td>{n.task_id && n.task_name ? <Link to={`/tasks/${n.task_id}`}>{n.task_name}</Link> : '—'}</td>
                            <td className="client-notes-note-cell">{n.note}</td>
                            <td className="client-notes-files-cell">
                              {(n.attachments ?? []).length === 0 ? (
                                '—'
                              ) : (
                                <ul className="client-notes-file-list">
                                  {(n.attachments ?? []).map((a) => (
                                    <li
                                      key={a.id}
                                      className={`client-notes-file-item${isVoided(a) ? ' attachment-voided' : ''}`}
                                    >
                                      <a
                                        href={clientIdNum != null ? clientNoteAttachmentDownloadUrl(clientIdNum, n.id, a.id) : '#'}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="client-notes-file-link attachment-void-name"
                                      >
                                        {a.original_file_name}
                                      </a>
                                      {isVoided(a) ? <AttachmentVoidPill /> : null}
                                      {user && clientNoteAuthorIsCurrentUser(n, user) ? (
                                        <button
                                          type="button"
                                          className={isVoided(a) ? 'attachment-unvoid-btn' : 'attachment-void-btn'}
                                          disabled={noteAttachmentVoid.busy}
                                          onClick={() =>
                                            isVoided(a)
                                              ? noteAttachmentVoid.unvoid(a)
                                              : noteAttachmentVoid.requestVoid(a)
                                          }
                                        >
                                          {isVoided(a) ? 'Unvoid' : 'Void'}
                                        </button>
                                      ) : null}
                                      <AttachmentVoidReason attachment={a} />
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </td>
                            <td className="client-notes-actions-cell">
                              <div className="client-notes-actions-inner">
                                {user && clientNoteAuthorIsCurrentUser(n, user) ? (
                                  <button
                                    type="button"
                                    className="client-action-btn client-action-btn--edit"
                                    data-tooltip="Edit note"
                                    aria-label="Edit note"
                                    onClick={() => {
                                      setEditingNoteId(n.id);
                                      setEditNoteText(n.note);
                                      setEditNoteTaskId(n.task_id ?? '');
                                      setNoteEditError('');
                                      setIsEditNoteModalOpen(true);
                                    }}
                                  >
                                    <FontAwesomeIcon icon={faPenToSquare} />
                                  </button>
                                ) : null}
                                {user && clientNoteAuthorIsCurrentUser(n, user) ? (
                                  <button
                                    type="button"
                                    className="client-action-btn client-action-btn--delete"
                                    data-tooltip="Delete note"
                                    aria-label="Delete note"
                                    onClick={() => {
                                      setDeletingNoteId(n.id);
                                      setNoteDeleteError('');
                                      setIsDeleteNoteModalOpen(true);
                                    }}
                                  >
                                    <FontAwesomeIcon icon={faTrash} />
                                  </button>
                                ) : null}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>

      {/* Upload a file straight into the client's folder */}
      <Modal
        isOpen={isUploadAttachmentModalOpen}
        onClose={() => !attachmentUploading && setIsUploadAttachmentModalOpen(false)}
        title="Upload File"
        narrow
      >
        <div className="modal-body">
          <p style={{ margin: '0 0 0.75rem', color: '#4b5563', fontSize: '0.9rem' }}>
            Files uploaded here are stored with this client. Contracts, invoices, sign-offs, note files, and survey
            responses appear in the list automatically.
          </p>
          <FileDropZone
            multiple
            disabled={attachmentUploading}
            onFiles={(files) => void handleUploadClientAttachments(files)}
            maxFileSizeBytes={50 * 1024 * 1024}
            onReject={(msg) => setAttachmentError(msg)}
            hint="Up to 50 MB per file"
          />
          {attachmentUploading ? <p style={{ margin: '0.75rem 0 0', color: '#6b7280' }}>Uploading…</p> : null}
          {attachmentError ? (
            <p style={{ color: '#b91c1c', margin: '0.75rem 0 0', fontSize: '0.875rem' }}>{attachmentError}</p>
          ) : null}
        </div>
        <div className="modal-actions">
          <button
            type="button"
            className="cancel-button"
            onClick={() => setIsUploadAttachmentModalOpen(false)}
            disabled={attachmentUploading}
          >
            Close
          </button>
        </div>
      </Modal>

      {/* Inline viewer for images and PDFs */}
      <Modal
        isOpen={previewAttachment != null}
        onClose={() => setPreviewAttachment(null)}
        title={previewAttachment?.name ?? 'File'}
      >
        <div className="modal-body client-attachment-preview">
          {previewAttachment && clientIdNum != null ? (
            attachmentPreviewKind(previewAttachment) === 'image' ? (
              <img
                src={clientAttachmentFileUrl(clientIdNum, previewAttachment)}
                alt={previewAttachment.name}
                className="client-attachment-preview__image"
              />
            ) : (
              <iframe
                title={previewAttachment.name}
                src={clientAttachmentFileUrl(clientIdNum, previewAttachment)}
                className="client-attachment-preview__frame"
              />
            )
          ) : null}
        </div>
        <div className="modal-actions">
          {previewAttachment && clientIdNum != null ? (
            <>
              <a
                className="cancel-button"
                href={clientAttachmentFileUrl(clientIdNum, previewAttachment)}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open in New Tab
              </a>
              <a
                className="cancel-button"
                href={clientAttachmentFileUrl(clientIdNum, previewAttachment, { download: true })}
              >
                Download
              </a>
            </>
          ) : null}
          <button type="button" className="generate-contract-button" onClick={() => setPreviewAttachment(null)}>
            Close
          </button>
        </div>
      </Modal>

      {/* Create Order Modal (pre-locked to this client) */}
      <Modal isOpen={isCreateOrderModalOpen} onClose={() => setIsCreateOrderModalOpen(false)} title="Create Order">
        {apiClient ? (
          <CreateOrderForm
            onClose={() => setIsCreateOrderModalOpen(false)}
            lockedClient={{
              company: apiClient.company,
              employeeName: apiClient.employee_name ?? undefined,
              userId: verifiedUsers.find((u) => u.username === apiClient.employee_name)?.id ?? null,
            }}
          />
        ) : null}
      </Modal>

      {/* Void Client Modal */}
      <Modal isOpen={voidModalOpen} onClose={closeVoidModal} title="Void Client" narrow>
        <div className="modal-body">
          <p style={{ marginBottom: '1rem' }}>
            Voiding <strong>{company}</strong> will move them to the Void Clients table.
            Please provide a reason — it will be shown on the Client Information card.
          </p>
          <div className="form-group">
            <label htmlFor="void-reason" className="form-label">Reason *</label>
            <textarea
              id="void-reason"
              className="form-textarea"
              value={voidReasonInput}
              onChange={(e) => {
                setVoidReasonInput(e.target.value);
                setVoidError('');
              }}
              rows={4}
              placeholder="Why is this client being voided?"
              disabled={voidSubmitting}
              autoFocus
            />
          </div>
          {voidError ? (
            <p className="create-order-error" role="alert">{voidError}</p>
          ) : null}
        </div>
        <div className="modal-actions">
          <button type="button" className="cancel-button" disabled={voidSubmitting} onClick={closeVoidModal}>
            Cancel
          </button>
          <button
            type="button"
            className="client-void-confirm-btn"
            disabled={voidSubmitting || !voidReasonInput.trim()}
            onClick={handleVoidClient}
          >
            {voidSubmitting ? 'Voiding…' : 'Void Client'}
          </button>
        </div>
      </Modal>

      {/* Add Note Modal */}
      <Modal
        isOpen={isAddNoteModalOpen}
        onClose={() => { setIsAddNoteModalOpen(false); setNewNote(''); setNewNoteTaskId(''); setNewNoteFile(null); setNoteError(''); }}
        title="Add Note"
        narrow
      >
        <div className="modal-body">
          <div className="form-group">
            <label htmlFor="new-note" className="form-label">Note</label>
            <textarea
              id="new-note"
              className="form-textarea"
              value={newNote}
              onChange={(e) => { setNewNote(e.target.value); setNoteError(''); }}
              rows={4}
              placeholder="Enter a note..."
              disabled={noteSubmitting}
            />
          </div>
          <div className="form-group">
            <span className="form-label">Attach file (optional)</span>
            <FileDropZone
              id="client-note-file-add"
              variant="compact"
              disabled={noteSubmitting}
              hint="Drag a file here or browse — one file."
              onFiles={(files) => setNewNoteFile(files[0] ?? null)}
            />
            {newNoteFile ? (
              <p className="client-note-file-chosen">{newNoteFile.name}</p>
            ) : null}
          </div>
          <div className="form-group">
            <label htmlFor="new-note-task" className="form-label">Attach to task (optional)</label>
            <select
              id="new-note-task"
              className="form-select"
              value={newNoteTaskId}
              onChange={(e) => setNewNoteTaskId(e.target.value === '' ? '' : Number(e.target.value))}
              disabled={noteSubmitting}
            >
              <option value="">None</option>
              {clientTasks.map((t) => (
                <option key={t.id} value={t.id}>{t.name} ({t.status})</option>
              ))}
            </select>
            {clientTasks.length === 0 && (
              <p style={{ marginTop: '0.25rem', fontSize: '0.875rem', color: 'var(--text-muted, #666)' }}>
                No tasks linked to this client yet.
              </p>
            )}
          </div>
          {noteError && <p className="create-order-error" role="alert">{noteError}</p>}
        </div>
        <div className="modal-actions">
          <button
            type="button"
            className="cancel-button"
            disabled={noteSubmitting}
            onClick={() => { setIsAddNoteModalOpen(false); setNewNote(''); setNewNoteTaskId(''); setNewNoteFile(null); setNoteError(''); }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="update-button"
            disabled={noteSubmitting || !newNote.trim() || !user}
            onClick={async () => {
              if (!clientIdNum || !user) return;
              setNoteError('');
              setNoteSubmitting(true);
              try {
                const taskId = newNoteTaskId === '' ? undefined : newNoteTaskId;
                let created = await createClientNote(clientIdNum, newNote.trim(), user.id, taskId);
                if (newNoteFile) {
                  const att = await uploadClientNoteAttachment(clientIdNum, created.id, newNoteFile, user.id);
                  created = { ...created, attachments: [...(created.attachments ?? []), att] };
                }
                setClientNotes((prev) => [created, ...prev]);
                setNewNote('');
                setNewNoteTaskId('');
                setNewNoteFile(null);
                setIsAddNoteModalOpen(false);
              } catch (err) {
                setNoteError(err instanceof Error ? err.message : 'Failed to add note.');
              } finally {
                setNoteSubmitting(false);
              }
            }}
          >
            {noteSubmitting ? 'Submitting…' : 'Submit Note'}
          </button>
        </div>
      </Modal>

      {/* Edit Note Modal */}
      <Modal
        isOpen={isEditNoteModalOpen}
        onClose={() => { setIsEditNoteModalOpen(false); setEditingNoteId(null); setEditNoteText(''); setEditNoteTaskId(''); setEditNoteExtraFile(null); setNoteEditError(''); }}
        title="Edit Note"
        narrow
      >
        <div className="modal-body">
          <div className="form-group">
            <label htmlFor="edit-note-text" className="form-label">Note</label>
            <textarea
              id="edit-note-text"
              className="form-textarea"
              value={editNoteText}
              onChange={(e) => { setEditNoteText(e.target.value); setNoteEditError(''); }}
              rows={4}
              placeholder="Enter note..."
              disabled={noteUpdating}
            />
          </div>
          {editingNoteId != null && clientIdNum != null ? (
            <div className="form-group">
              <span className="form-label">Files on this note</span>
              {(clientNotes.find((x) => x.id === editingNoteId)?.attachments ?? []).length === 0 ? (
                <p className="client-note-edit-files-empty">No files yet. Add one below.</p>
              ) : (
                <ul className="client-notes-file-list client-notes-file-list--modal">
                  {(clientNotes.find((x) => x.id === editingNoteId)?.attachments ?? []).map((a) => (
                    <li
                      key={a.id}
                      className={`client-notes-file-item${isVoided(a) ? ' attachment-voided' : ''}`}
                    >
                      <a
                        href={clientNoteAttachmentDownloadUrl(clientIdNum, editingNoteId, a.id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="client-notes-file-link attachment-void-name"
                      >
                        {a.original_file_name}
                      </a>
                      {isVoided(a) ? <AttachmentVoidPill /> : null}
                      {user ? (
                        <button
                          type="button"
                          className={isVoided(a) ? 'attachment-unvoid-btn' : 'attachment-void-btn'}
                          disabled={noteUpdating || noteAttachmentVoid.busy}
                          onClick={() =>
                            isVoided(a) ? noteAttachmentVoid.unvoid(a) : noteAttachmentVoid.requestVoid(a)
                          }
                        >
                          {isVoided(a) ? 'Unvoid' : 'Void'}
                        </button>
                      ) : null}
                      <AttachmentVoidReason attachment={a} />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : null}
          <div className="form-group">
            <span className="form-label">Add file (optional)</span>
            <FileDropZone
              id="client-note-file-edit"
              variant="compact"
              disabled={noteUpdating}
              hint="Drag a file here or browse — one file."
              onFiles={(files) => setEditNoteExtraFile(files[0] ?? null)}
            />
            {editNoteExtraFile ? (
              <p className="client-note-file-chosen">{editNoteExtraFile.name}</p>
            ) : null}
          </div>
          <div className="form-group">
            <label htmlFor="edit-note-task" className="form-label">Attach to task (optional)</label>
            <select
              id="edit-note-task"
              className="form-select"
              value={editNoteTaskId}
              onChange={(e) => setEditNoteTaskId(e.target.value === '' ? '' : Number(e.target.value))}
              disabled={noteUpdating}
            >
              <option value="">None</option>
              {clientTasks.map((t) => (
                <option key={t.id} value={t.id}>{t.name} ({t.status})</option>
              ))}
            </select>
          </div>
          {noteEditError && <p className="create-order-error" role="alert">{noteEditError}</p>}
        </div>
        <div className="modal-actions">
          <button
            type="button"
            className="cancel-button"
            disabled={noteUpdating}
            onClick={() => { setIsEditNoteModalOpen(false); setEditingNoteId(null); setEditNoteText(''); setEditNoteTaskId(''); setEditNoteExtraFile(null); setNoteEditError(''); }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="update-button"
            disabled={noteUpdating || !editNoteText.trim() || !user}
            onClick={async () => {
              if (!clientIdNum || !user || !editingNoteId) return;
              setNoteEditError('');
              setNoteUpdating(true);
              try {
                const taskId = editNoteTaskId === '' ? undefined : editNoteTaskId;
                let updated = await updateClientNote(clientIdNum, editingNoteId, editNoteText.trim(), user.id, taskId);
                if (editNoteExtraFile) {
                  const att = await uploadClientNoteAttachment(clientIdNum, editingNoteId, editNoteExtraFile, user.id);
                  updated = { ...updated, attachments: [...(updated.attachments ?? []), att] };
                }
                setClientNotes((prev) => prev.map((note) => (note.id === editingNoteId ? updated : note)));
                setIsEditNoteModalOpen(false);
                setEditingNoteId(null);
                setEditNoteText('');
                setEditNoteTaskId('');
                setEditNoteExtraFile(null);
              } catch (err) {
                setNoteEditError(err instanceof Error ? err.message : 'Failed to update note.');
              } finally {
                setNoteUpdating(false);
              }
            }}
          >
            {noteUpdating ? 'Saving…' : 'Save'}
          </button>
        </div>
      </Modal>

      {/* Delete Note Confirmation Modal */}
      <Modal
        isOpen={isDeleteNoteModalOpen}
        onClose={() => { setIsDeleteNoteModalOpen(false); setDeletingNoteId(null); setNoteDeleteError(''); }}
        title="Delete Note"
        narrow
      >
        <div className="modal-body">
          <p style={{ marginBottom: '1rem' }}>Are you sure you want to delete this note? This action cannot be undone.</p>
          {noteDeleteError && <p className="create-order-error" role="alert">{noteDeleteError}</p>}
        </div>
        <div className="modal-actions">
          <button
            type="button"
            className="cancel-button"
            disabled={noteDeleting}
            onClick={() => { setIsDeleteNoteModalOpen(false); setDeletingNoteId(null); setNoteDeleteError(''); }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="client-delete-confirm-btn"
            disabled={noteDeleting}
            onClick={async () => {
              if (!clientIdNum || !user || !deletingNoteId) return;
              setNoteDeleting(true);
              setNoteDeleteError('');
              try {
                await deleteClientNote(clientIdNum, deletingNoteId, user.id);
                setClientNotes((prev) => prev.filter((n) => n.id !== deletingNoteId));
                setIsDeleteNoteModalOpen(false);
                setDeletingNoteId(null);
              } catch (err) {
                setNoteDeleteError(err instanceof Error ? err.message : 'Failed to delete note.');
              } finally {
                setNoteDeleting(false);
              }
            }}
          >
            {noteDeleting ? 'Deleting…' : 'Delete Note'}
          </button>
        </div>
      </Modal>

      {/* Combined Edit Modal */}
      <Modal isOpen={isEditModalOpen} onClose={handleEditCancel} title="Edit Client Information">
        <div className="modal-body">
          <div className="form-group">
            <label htmlFor="edit-company" className="form-label">Company</label>
            <input
              type="text"
              id="edit-company"
              className="form-input"
              value={editCompany}
              onChange={(e) => setEditCompany(e.target.value)}
              placeholder="Company name"
            />
          </div>
          <div className="form-group">
            <label htmlFor="edit-employee" className="form-label">Employee</label>
            <SearchableDropdown
              options={verifiedUsers.map((u) => u.username)}
              value={editEmployee}
              onChange={(value) => {
                setEditEmployee(value);
                const u = verifiedUsers.find((x) => x.username === value);
                setEditSelectedUserId(u ? u.id : null);
              }}
              placeholder="Select employee..."
            />
          </div>
          <div className="form-group">
            <label htmlFor="edit-start-date" className="form-label">Start Date</label>
            <input
              type="date"
              id="edit-start-date"
              className="form-input"
              value={editStartDate}
              onChange={(e) => setEditStartDate(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label htmlFor="edit-first-contact" className="form-label">First Contact</label>
            <input
              type="date"
              id="edit-first-contact"
              className="form-input"
              value={editFirstContact}
              onChange={(e) => setEditFirstContact(e.target.value)}
            />
            <p className="form-hint">Date of the first outreach to this company.</p>
          </div>
          <div className="form-group">
            <label htmlFor="edit-industry" className="form-label">Industry</label>
            <SearchableDropdown
              options={INDUSTRIES}
              value={editIndustry}
              onChange={setEditIndustry}
              placeholder="Type to search or enter industry..."
            />
          </div>

          <hr style={{ margin: '1.25rem 0', borderColor: '#e5e7eb' }} />

          <div className="form-group">
            <label htmlFor="edit-point-of-contact" className="form-label">Point of Contact</label>
            <input
              type="text"
              id="edit-point-of-contact"
              className="form-input"
              value={editPointOfContact}
              onChange={(e) => setEditPointOfContact(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label htmlFor="edit-contact-email" className="form-label">Email</label>
            <input
              type="email"
              id="edit-contact-email"
              className="form-input"
              value={editContactEmail}
              onChange={(e) => setEditContactEmail(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label htmlFor="edit-contact-phone" className="form-label">Phone</label>
            <input
              type="tel"
              id="edit-contact-phone"
              className="form-input"
              value={editContactPhone}
              onChange={(e) => setEditContactPhone(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label htmlFor="edit-billing-address" className="form-label">Billing Address</label>
            <textarea
              id="edit-billing-address"
              className="form-textarea"
              value={editBillingAddress}
              onChange={(e) => setEditBillingAddress(e.target.value)}
              rows={3}
              placeholder="Enter billing address..."
            />
          </div>
          <div className="form-group">
            <label htmlFor="edit-site-location-address" className="form-label">Site Location Address</label>
            <textarea
              id="edit-site-location-address"
              className="form-textarea"
              value={editSiteLocationAddress}
              onChange={(e) => setEditSiteLocationAddress(e.target.value)}
              rows={3}
              placeholder="Enter site location address..."
            />
          </div>
          <div className="form-group">
            <label htmlFor="edit-state-code" className="form-label">State</label>
            <select
              id="edit-state-code"
              className="form-input"
              value={editStateCode}
              onChange={(e) => setEditStateCode(e.target.value)}
            >
              <option value="">Select a state…</option>
              {US_STATES.map((s) => (
                <option key={s.code} value={s.code}>
                  {s.name}
                </option>
              ))}
            </select>
            <p className="form-hint">
              {editRegion
                ? `${editRegion.label} region — job code starts with ${editRegion.letter}.`
                : 'Sets the region letter of this client’s Job Code / Deployment ID.'}
            </p>
          </div>

          <hr style={{ margin: '1.25rem 0', borderColor: '#e5e7eb' }} />

          <div className="form-group">
            <label htmlFor="edit-notes" className="form-label">Notes</label>
            <textarea
              id="edit-notes"
              className="form-textarea"
              value={editNotes}
              onChange={(e) => setEditNotes(e.target.value)}
              rows={4}
              placeholder="Additional notes about this client..."
            />
          </div>

          {saveError && <p className="create-order-error" role="alert" style={{ marginTop: '0.5rem' }}>{saveError}</p>}
        </div>
        <div className="modal-actions client-edit-modal-actions">
          {/* Left of Cancel/Save because it is a correction, not part of saving.
              Hidden for a voided client: restore it first, then demote. */}
          {!isVoid && (
            <button
              type="button"
              className="client-demote-button"
              onClick={openConvertModal}
              disabled={saving}
            >
              Convert to Lead
            </button>
          )}
          <button type="button" className="cancel-button" onClick={handleEditCancel} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="update-button" onClick={handleEditSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </Modal>

      {/* Convert Client → Lead. Stacked above the edit modal it was opened from. */}
      <Modal
        isOpen={convertModalOpen}
        onClose={closeConvertModal}
        title="Convert Client to Lead"
        narrow
        elevated
      >
        <div className="modal-body">
          <p style={{ marginBottom: '1rem' }}>
            Move <strong>{company}</strong> back to the Leads list? Everything already on the
            record — notes, files, orders and appointments — stays attached, and its job code is
            held in case it becomes a client again.
          </p>
          {attachedWorkSummary && (
            <p className="client-demote-warning" role="status">
              <strong>Heads up:</strong> this client has {attachedWorkSummary} on file. Converting
              it to a lead does not remove any of that, but a lead is not expected to have active
              work, so check it is what you intend.
            </p>
          )}
          <div className="form-group">
            <label htmlFor="convert-source" className="form-label">
              Source {convertNeedsSource ? '*' : ''}
            </label>
            <input
              id="convert-source"
              type="text"
              className="form-input"
              value={convertSource}
              onChange={(e) => {
                setConvertSource(e.target.value);
                setConvertError('');
              }}
              placeholder="e.g. Website Inquiry, Referral"
              disabled={convertSubmitting}
            />
            <p className="form-hint">
              {convertNeedsSource
                ? 'Leads are tracked by where they came from, and this record does not have one yet.'
                : 'Carried over from when this was a lead. Edit it if it has changed.'}
            </p>
          </div>
          {convertError ? (
            <p className="create-order-error" role="alert">{convertError}</p>
          ) : null}
        </div>
        <div className="modal-actions">
          <button
            type="button"
            className="cancel-button"
            disabled={convertSubmitting}
            onClick={closeConvertModal}
          >
            Cancel
          </button>
          <button
            type="button"
            className="client-convert-confirm-btn"
            disabled={convertSubmitting || (convertNeedsSource && !convertSource.trim())}
            onClick={handleConvertToLead}
          >
            {convertSubmitting ? 'Converting…' : 'Convert to Lead'}
          </button>
        </div>
      </Modal>

      <AttachmentVoidModal control={folderAttachmentVoid} />
      {/* Note files can be voided from inside the edit-note dialog, so stack above it. */}
      <AttachmentVoidModal control={noteAttachmentVoid} elevated />
    </div>
  );
};

export default ClientDetail;
